import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { detectIdentityConflicts, reconcileUsers } from '../utils/identityEngine';
import { parseIdentityCSV, generateCSVTemplate } from '../utils/csvHelper';
import supabase from '../supabase/supabase';

const { FiShield, FiAlertTriangle, FiCheckCircle, FiUpload, FiFileText, FiRefreshCw, FiUserCheck, FiTarget, FiDownload, FiCode } = FiIcons;

const IdentityResolution = () => {
  const { analyticsData, customUsers, fetchCustomUsers } = useAnalytics();
  const [conflicts, setConflicts] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState('');
  const [importFormat, setImportFormat] = useState('csv');
  const [importReport, setImportReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const detected = detectIdentityConflicts(analyticsData, customUsers);
    setConflicts(detected);
  }, [analyticsData, customUsers]);

  const handleBulkImport = async () => {
    setLoading(true);
    try {
      let parsed;
      if (importFormat === 'csv') {
        parsed = await parseIdentityCSV(importData);
      } else {
        parsed = JSON.parse(importData);
      }
      const { toUpsert, report } = reconcileUsers(customUsers, Array.isArray(parsed) ? parsed : [parsed]);
      
      if (toUpsert.length > 0) {
        const { error } = await supabase.from('custom_users_1740000000000').upsert(toUpsert, { onConflict: 'user_serial' });
        if (error) throw error;
      }
      setImportReport(report);
      fetchCustomUsers();
    } catch (e) {
      alert("Import Failed: " + (e.message || "Invalid format"));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = generateCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'intuitus_identity_template.csv';
    a.click();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
            <SafeIcon icon={FiShield} className="text-xl" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Governance & Resolution</h2>
            <p className="text-xs text-slate-500 font-medium">Neural conflict detection v2.1</p>
          </div>
        </div>
        <button 
          onClick={() => setShowImport(true)}
          className="px-4 py-2 bg-slate-900 text-white rounded-lg font-bold text-xs flex items-center gap-2 hover:bg-slate-800 transition-all shadow-sm"
        >
          <SafeIcon icon={FiUpload} /> Bulk Import Data
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider px-2">Anomaly Matrix</h3>
            <span className="bg-rose-50 text-rose-600 px-2 py-0.5 rounded-md text-[10px] font-bold border border-rose-100">
              {conflicts.length} Active Conflicts
            </span>
          </div>

          {conflicts.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheckCircle} className="text-xl" />
              </div>
              <h4 className="font-bold text-slate-900">No Conflicts Detected</h4>
              <p className="text-slate-500 text-xs mt-1">Identity telemetry is perfectly synchronized.</p>
            </div>
          ) : (
            conflicts.map((conflict, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 transition-colors"
              >
                <div className="flex gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    conflict.severity === 'high' ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600'
                  }`}>
                    <SafeIcon icon={FiAlertTriangle} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <span className="text-[10px] font-bold uppercase text-blue-600 tracking-wider">{conflict.type.replace('_', ' ')}</span>
                      <div className="flex gap-1">
                        <button className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"><SafeIcon icon={FiUserCheck} /></button>
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded transition-all"><SafeIcon icon={FiTarget} /></button>
                      </div>
                    </div>
                    <h4 className="font-bold text-slate-900 text-sm mt-0.5">{conflict.description}</h4>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {conflict.affected_ids.map(id => (
                        <span key={id} className="text-[9px] font-mono font-bold bg-slate-50 px-2 py-0.5 rounded border border-slate-100 text-slate-500">
                          {id.substring(0, 12)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 rounded-xl p-6 text-white shadow-lg">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
              <SafeIcon icon={FiRefreshCw} className="text-blue-400" /> Resolution Logic
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs p-2 bg-white/5 rounded-lg">
                <span className="text-slate-400">Email Stitching</span>
                <span className="text-emerald-400 font-bold">ACTIVE</span>
              </div>
              <div className="flex items-center justify-between text-xs p-2 bg-white/5 rounded-lg">
                <span className="text-slate-400">Multi-Account Check</span>
                <span className="text-emerald-400 font-bold">ACTIVE</span>
              </div>
              <div className="flex items-center justify-between text-xs p-2 bg-white/5 rounded-lg">
                <span className="text-slate-400">Orphaned Cleanup</span>
                <span className="text-emerald-400 font-bold">ACTIVE</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Identification Health</h4>
            <div className="text-3xl font-bold text-slate-900">98.4%</div>
            <div className="w-full bg-slate-100 h-2 rounded-full mt-4">
              <div className="bg-blue-600 h-full rounded-full transition-all duration-1000" style={{ width: '98.4%' }}></div>
            </div>
            <p className="text-[10px] text-slate-500 mt-4 leading-relaxed">
              Based on {analyticsData.pageViews.length} session signals processed in the last 24h.
            </p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showImport && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200"
            >
              <div className="p-6 border-b flex justify-between items-center bg-slate-50">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Bulk Identity Sync</h3>
                  <p className="text-slate-500 text-xs font-medium mt-1">Reconcile external records with Intuitus telemetry</p>
                </div>
                <button onClick={() => { setShowImport(false); setImportReport(null); }} className="text-slate-400 hover:text-rose-500 transition-all">
                  <SafeIcon icon={FiIcons.FiX} className="text-xl" />
                </button>
              </div>

              <div className="p-6">
                {!importReport ? (
                  <div className="space-y-4">
                    <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit">
                      <button 
                        onClick={() => setImportFormat('csv')}
                        className={`px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                          importFormat === 'csv' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'
                        }`}
                      >
                        <SafeIcon icon={FiFileText} className="inline mr-2" /> CSV
                      </button>
                      <button 
                        onClick={() => setImportFormat('json')}
                        className={`px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${
                          importFormat === 'json' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'
                        }`}
                      >
                        <SafeIcon icon={FiCode} className="inline mr-2" /> JSON
                      </button>
                    </div>

                    <div className="relative">
                      <textarea 
                        className="w-full h-48 bg-slate-50 border border-slate-200 rounded-lg p-4 font-mono text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none transition-all"
                        placeholder={importFormat === 'csv' ? 'sourceUserID,firstName,lastName,sourceEmail...' : '[{"sourceUserID": "..."}]'}
                        value={importData}
                        onChange={(e) => setImportData(e.target.value)}
                      />
                      {importFormat === 'csv' && (
                        <button 
                          onClick={downloadTemplate}
                          className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-[10px] font-bold text-blue-600 hover:bg-blue-50 transition-all shadow-sm"
                        >
                          <SafeIcon icon={FiDownload} /> Sample CSV
                        </button>
                      )}
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                      <button onClick={() => setShowImport(false)} className="px-4 py-2 text-slate-500 text-xs font-bold uppercase tracking-widest">Cancel</button>
                      <button 
                        onClick={handleBulkImport}
                        disabled={loading || !importData}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold uppercase text-[10px] tracking-widest shadow-lg shadow-blue-100 disabled:opacity-50"
                      >
                        {loading ? 'Processing...' : 'Execute Sync'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiCheckCircle} className="text-2xl" />
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 mb-6">Reconciliation Complete</h4>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                        <div className="text-xl font-bold text-blue-600">{importReport.created}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase">New</div>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                        <div className="text-xl font-bold text-emerald-600">{importReport.reconciled}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase">Updated</div>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                        <div className="text-xl font-bold text-slate-400">{importReport.identical}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase">Skipped</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => { setShowImport(false); setImportReport(null); }}
                      className="px-8 py-2 bg-slate-900 text-white rounded-lg font-bold uppercase text-[10px] tracking-widest"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IdentityResolution;